import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Link,
  CardMedia
} from '@mui/material';
import { useLoadScript, GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import { useCityContext } from '../contexts/CityContext';

const mapContainerStyle = {
  width: '100%',
  height: '70vh',
  borderRadius: '8px'
};

const MapView = ({ deals, onClose }) => {
  const { city } = useCityContext();
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [markerIcon, setMarkerIcon] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    // Initialize marker icon after map loads
    if (window.google) {
      setMarkerIcon({
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: '#FF5252',
        fillOpacity: 1,
        strokeWeight: 1,
        strokeColor: '#FFFFFF',
        scale: 2,
        anchor: new window.google.maps.Point(12, 24),
      });
    }
  }, []);

  const handleMarkerClick = (venue) => {
    setSelectedVenue(venue);
  };

  if (!isLoaded) {
    return <CircularProgress />;
  }

  return (
    <Paper elevation={3} sx={{ p: 2, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Happy Hours Map</Typography>
        <Button 
          onClick={onClose}
          startIcon={<CloseIcon />}
          variant="outlined"
        >
          Close Map
        </Button>
      </Box>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={city.defaultCenter}
        zoom={13}
        onLoad={onMapLoad}
      >
        {deals.map((venue) => (
          <MarkerF
            key={venue.id}
            position={{ lat: venue.lat, lng: venue.lon }}
            onClick={() => handleMarkerClick(venue)}
            icon={markerIcon}
            animation={window.google?.maps.Animation.DROP}
          />
        ))}

        {selectedVenue && (
          <InfoWindowF
            position={{ lat: selectedVenue.lat, lng: selectedVenue.lon }}
            onCloseClick={() => setSelectedVenue(null)}
          >
            <Box sx={{ p: 1, maxWidth: 280 }}>
              {selectedVenue.image_url && (
                <CardMedia
                  component="img"
                  height="140"
                  image={selectedVenue.image_url}
                  alt={selectedVenue.headline}
                  sx={{ 
                    borderRadius: 1,
                    mb: 1,
                    objectFit: 'cover'
                  }}
                />
              )}

              <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>
                {selectedVenue.headline}
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: 'primary.main',
                  fontWeight: 'medium'
                }}
              >
                {selectedVenue.main_offer}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {selectedVenue.days}
                </Typography>
              </Box>

              {selectedVenue.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Link 
                    href={`tel:${selectedVenue.phone}`}
                    sx={{ color: 'primary.main' }}
                  >
                    {selectedVenue.phone}
                  </Link>
                </Box>
              )}

              {selectedVenue.link && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LanguageIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Link 
                    href={selectedVenue.link} 
                    target="_blank" 
                    rel="noopener"
                    sx={{ color: 'primary.main' }}
                  >
                    Website
                  </Link>
                </Box>
              )}
            </Box>
          </InfoWindowF>
        )}
      </GoogleMap>
    </Paper>
  );
};

export default MapView;